import React from "react";
import "./CorporateDashboard.css";
import Sidebar from "../../../Navigation/Sidebar/Sidebar";
import { useNavigate, useParams } from "react-router-dom";

const CorporateDashboard = ({ dashboardData, loading }) => {
  const navigate = useNavigate();
  const { corporateId } = useParams(); // Get corporateId from URL

  if (loading) {
    return (
      <div className="corporate-dashboard-container">
        <div className="sidebar-container">
          <Sidebar />
        </div>
        <div className="corporate-dashboard-content">
          <div className="top-navigation">
            <div className="corporate-dashboardnav-left">
              <button className="nav-back-btn" onClick={() => navigate(-1)}>
                ←
              </button>
              <h2 className="nav-title">Corporate Dashboard</h2>
            </div>
          </div>
          <div className="main-content">
            <div className="welcome-loading-spinner">
              Loading dashboard data...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="corporate-dashboard-container">
        <div className="sidebar-container">
          <Sidebar />
        </div>
        <div className="corporate-dashboard-content">
          <div className="top-navigation">
            <div className="corporate-dashboardnav-left">
              <button className="nav-back-btn" onClick={() => navigate(-1)}>
                ←
              </button>
              <h2 className="nav-title">Corporate Dashboard</h2>
            </div>
          </div>
          <div className="main-content">
            <div className="error-message">
              Unable to load dashboard data. Please try again.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default values if no data
  const stats = dashboardData || {
    corporateId: corporateId || 0,
    corporateName: "Corporate",
    departments: 0,
    users: 0,
    rules: 0,
    usageLimit: {
      value: 0,
      limitOn: "amount",
      frequency: "1",
      usageLimitRequired: true
    }
  };

  // Navigation handlers for each card
  const handleDepartmentsClick = () => {
    navigate(`/home/customermanager/departments/${corporateId}`);
  };

  const handleUsersClick = () => {
    navigate(`/home/customermanager/bpmanagementlist/corporatemanagementuserdetails/${corporateId}`);
  };

  const handleRulesClick = () => {
    navigate(`/home/customermanager/corporate/rules/${corporateId}`);
  };

  const handleUsageLimitClick = () => {
    navigate(`/home/customermanager/usage-limit/${corporateId}`);
  };

  const handleRechargeHistory = () => {
    navigate(`/home/customermanager/recharge-history/${corporateId}`);
  };

  // Quick Actions navigation
  const handleAddUser = () => {
    // Navigate to user creation page with corporateId
    navigate(`/home/customermanager/bpmanagementlist/corporatemanagementusercreation/${corporateId}`);
  };

  const handleAddRule = () => {
    // Navigate to rule creation page with corporateId
    navigate(`/home/customermanager/corporate/add-rule/${corporateId}`);
  };

  const handleExport = () => {
    // Handle export functionality
    console.log("Export functionality");
    // You can implement export logic here
    // For example: navigate(`/home/customermanager/export/${corporateId}`);
  };

  return (
    <div className="corporate-dashboard-container">
      {/* Sidebar on the left */}
      <div className="sidebar-container">
        <Sidebar />
      </div>

      {/* Main Content Area */}
      <div className="corporate-dashboard-content">
        {/* Top Navigation Bar with Title and Recharge Button */}
        <div className="top-navigation">
          <div className="corporate-dashboard-nav-left">
            <button className="nav-back-btn" onClick={() => navigate("/home/customermanager/corporatelist")}>
              ←
            </button>
            <h2 className="nav-title">Corporate Dashboard</h2>
          </div>
          
          <button className="recharge-history-btn" onClick={handleRechargeHistory}>
            <span>📋</span>
            Recharge History
          </button>
        </div>

        {/* Header with Light Green Background (only welcome message) */}
        <div className="dashboard-header">
          <p className="welcome-message">
            <span className="corporate-name-welcome">Welcome</span>    
            <span className="corporate-name">{stats.corporateName}</span>
          </p>
          <p className="dashboard-description">
            Manage your departments, users, and operational rules from a central overview. 
            Access quick actions to maintain your workspace efficiently.
          </p>
        </div>

        {/* Main Content Area */}
        <div className="main-content">
          {/* Stats Cards - Now clickable */}
          <div className="stats-grid">
            {/* Departments Card */}
            <div className="stat-card clickable" onClick={handleDepartmentsClick}>
              <div className="stat-card-content">
                <h3 className="stat-title">Departments</h3>
                <div className="stat-value">{stats.departments || 0}</div>
              </div>
              <button className="stat-action-btn" onClick={handleDepartmentsClick}>
                View Details 
              </button>
            </div>

            {/* Users Card */}
            <div className="stat-card clickable" onClick={handleUsersClick}>
              <div className="stat-card-content">
                <h3 className="stat-title">Users</h3>
                <div className="stat-value">{stats.users || 0}</div>
              </div>
              <button className="stat-action-btn" onClick={handleUsersClick}>
                View Details 
              </button>
            </div>

            {/* Rules Card */}
            <div className="stat-card clickable" onClick={handleRulesClick}>
              <div className="stat-card-content">
                <h3 className="stat-title">Rules</h3>
                <div className="stat-value">{stats.rules || 0}</div>
              </div>
              <button className="stat-action-btn" onClick={handleRulesClick}>
                View Details 
              </button>
            </div>

            {/* Usage Limit Card - FIXED */}
            <div className="stat-card clickable" onClick={handleUsageLimitClick}>
              <div className="stat-card-content">
                <h3 className="stat-title">Usage Limit</h3>
                <div className="stat-value">
                  {stats.usageLimit?.value ? `₹ ${stats.usageLimit.value.toLocaleString('en-IN')}` : "₹ 0"}
                </div>
                {/* {stats.usageLimit?.frequency && (
                  <div className="stat-subtitle">
                    {stats.usageLimit.frequency === "1" ? "Monthly" : `Every ${stats.usageLimit.frequency} months`}
                  </div>
                )} */}
              </div>
              <button className="stat-action-btn" onClick={handleUsageLimitClick}>
                View Details 
              </button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="quick-actions-section">
            <h2 className="section-title">Quick Actions</h2>
            <div className="quick-actions-grid">
              <button className="action-btn" onClick={handleAddUser}>
                <span className="action-icon">+</span>
                <span className="action-text">Add User</span>
              </button>
              
              <button className="action-btn" onClick={handleAddRule}>
                <span className="action-icon">+</span>
                <span className="action-text">Add Rule</span>
              </button>
              
              {/* <button className="action-btn" onClick={handleExport}>
                <span className="action-icon">📤</span>
                <span className="action-text">Export</span>
              </button> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorporateDashboard;